import type { ChangeEvent } from 'react';
import type {
    EditorActorRef,
    EditorContext,
    FileBrowserItem,
} from '@pyranger/types';
import {
    convertFileCacheToFileBrowserItems,
    getFormattedErrorMessage,
    debug,
} from '@pyranger/utils';
import {
    FILE_BROWSER_ROOT_INDEX,
    FILE_BROWSER_ROOT_ITEM,
    FILE_BROWSER_TREE_ID,
} from '@pyranger/constants';

import {
    readFileWithVerification,
    writeFileWithVerification,
    deleteFileOnDevice,
    deleteDirectoryOnDevice,
    createDirectoryOnDevice,
} from './fileOps';

// --- File Cache Helpers ---

export const addToFileCache = ({
    path,
    info,
    context,
}: {
    path: string;
    info: {
        size?: number;
        content?: string;
    };
    context: EditorContext;
}) => {
    if (context.ref.current.fileBrowser.fileCache === null) {
        context.ref.current.fileBrowser.fileCache = {};
    }

    if (path.endsWith('/')) {
        context.ref.current.fileBrowser.fileCache[path] = {
            size: info?.size ?? 0,
            type: 'directory',
        };
    } else {
        context.ref.current.fileBrowser.fileCache[path] = {
            type: 'file',
            size:
                info.size !== undefined
                    ? info.size
                    : info.content !== undefined
                      ? info.content.length
                      : 0,
            content: info.content || '',
        };
    }
};

// --- File Browser UI ---

export const updateFileBrowser = ({ context }: { context: EditorContext }) => {
    const {
        ref,
        settings,
        fileBrowser: { dataProvider, items },
    } = context;
    const {
        fileBrowser: { fileCache },
    } = ref.current;

    const nextItems = convertFileCacheToFileBrowserItems(fileCache, {
        foldersFirst: settings.fileBrowserFoldersFirst ?? true,
    });

    Object.keys(items).forEach((key) => {
        delete items[key];
    });

    Object.entries(nextItems).forEach(([key, value]) => {
        items[key] = value;
    });

    dataProvider.onDidChangeTreeDataEmitter.emit(Object.keys(nextItems));

    return true;
};

export const clearFileBrowser = async ({
    context,
}: {
    context: EditorContext;
}) => {
    const {
        fileBrowser: { items, dataProvider },
    } = context;

    Object.keys(items).forEach((key) => {
        delete items[key];
    });

    items[FILE_BROWSER_ROOT_INDEX] = FILE_BROWSER_ROOT_ITEM;
    dataProvider.onDidChangeTreeDataEmitter.emit([FILE_BROWSER_ROOT_INDEX]);
};

// --- File Operations (delegating to fileOps.ts via MpRawMode) ---

export const saveFile = async ({
    autoSave,
    context,
    path,
    content,
}: {
    autoSave: boolean;
    context: EditorContext;
    path: string;
    content: string;
}) => {
    try {
        const result = await writeFileWithVerification({
            context,
            path,
            content,
        });

        if (!result.success) {
            const errorMsg =
                result.error === 'integrity-mismatch'
                    ? `File integrity check failed for ${path} — device hash does not match browser hash after ${result.attempts} attempt(s)`
                    : result.error === 'communication-error'
                      ? `Communication error while verifying save of ${path}`
                      : `Save verification failed for ${path} after ${result.attempts} attempt(s)`;

            context.showCustomAlert({
                title: 'Save verification error',
                message: errorMsg,
                variant: 'error',
            });

            throw new Error(errorMsg);
        }

        addToFileCache({
            path,
            info: { size: result.size, content },
            context,
        });

        if (!autoSave) {
            context.showCustomAlert({
                title: 'Success',
                variant: 'success',
                message: `File at ${path} saved successfully, size: ${result.size} bytes`,
            });
        }

        return true;
    } catch (error) {
        const message = getFormattedErrorMessage(
            error,
            `Failed to save file at ${path}`,
        );

        context.showCustomAlert({
            title: 'Save error',
            message,
            variant: 'error',
        });

        throw new Error(message);
    }
};

export const deleteFile = async ({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
    port?: SerialPort; // kept for backward compat signature but unused
}) => {
    await deleteFileOnDevice({ context, path });

    if (context.ref.current.fileBrowser.fileCache) {
        delete context.ref.current.fileBrowser.fileCache[path];
    }

    return true;
};

export const createDirectory = async ({
    context,
    dirname,
}: {
    context: EditorContext;
    dirname: string;
    port?: SerialPort; // kept for backward compat signature but unused
}) => {
    await createDirectoryOnDevice({ context, dirname });

    // Add the new directory and all its parent directories to the file cache
    const parts = dirname.split('/');
    for (let i = 1; i <= parts.length; i++) {
        const dirPath = parts.slice(0, i).join('/') + '/';
        addToFileCache({ path: dirPath, info: { size: 0 }, context });
    }
};

export const deleteDirectory = async ({
    context,
    dirname,
}: {
    context: EditorContext;
    dirname: string;
    port?: SerialPort; // kept for backward compat signature but unused
}) => {
    await deleteDirectoryOnDevice({ context, path: dirname });

    // Remove from file cache
    if (context.ref.current.fileBrowser.fileCache) {
        const prefix = dirname.endsWith('/') ? dirname : dirname + '/';
        for (const key of Object.keys(
            context.ref.current.fileBrowser.fileCache,
        )) {
            if (key === prefix || key.startsWith(prefix)) {
                delete context.ref.current.fileBrowser.fileCache[key];
            }
        }
    }
};

/**
 * Load a file's content — from cache if available, otherwise from device with verification.
 */
export const loadFile = async ({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
    actorRef?: EditorActorRef; // kept for backward compat but unused
    port?: SerialPort; // kept for backward compat but unused
}) => {
    const {
        ref: {
            current: {
                fileBrowser: { fileCache },
            },
        },
    } = context;

    if (fileCache && fileCache[path] && fileCache[path].content) {
        return fileCache[path].content;
    }

    // Load from device with integrity verification
    const result = await readFileWithVerification({ context, path });

    if (!result.success) {
        const errorMsg =
            result.error === 'integrity-mismatch'
                ? `Transfer corruption detected for ${path} — file hash mismatch`
                : result.error === 'communication-error'
                  ? `Communication error while loading ${path}`
                  : `Failed to verify integrity of ${path}`;

        throw new Error(errorMsg);
    }

    const content = result.content;

    // Update cache
    if (context.ref.current.fileBrowser.fileCache === null) {
        context.ref.current.fileBrowser.fileCache = {};
    }

    context.ref.current.fileBrowser.fileCache[path] = {
        type: 'file',
        size: content.length,
        content,
    };

    return content;
};

// --- Delete helpers used by the fileBrowser actor ---

export const deleteFileBrowserDirectory = async ({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
}) => {
    const {
        fileBrowser: { items },
    } = context;

    try {
        await deleteDirectory({ context, dirname: path });
        delete items[path];
        return true;
    } catch (e) {
        const message = getFormattedErrorMessage(
            e,
            `Failed to delete directory ${path}`,
        );

        context.showCustomAlert({
            title: 'Delete Directory Error',
            message,
            variant: 'error',
        });

        return false;
    }
};

export const deleteFileBrowserFile = async ({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
}) => {
    const {
        fileBrowser: { items },
    } = context;

    try {
        await deleteFile({ context, path });
        delete items[path];
        return true;
    } catch (e) {
        const message = getFormattedErrorMessage(
            e,
            `Failed to delete file ${path}`,
        );

        console.error(message);

        context.showCustomAlert({
            title: 'Delete File Error',
            message,
            variant: 'error',
        });

        return false;
    }
};

// --- Item selection and file loading ---

export const handleItemSelect = async ({
    actorRef,
    context,
    item,
}: {
    actorRef: EditorActorRef;
    context: EditorContext;
    item: FileBrowserItem | null;
}) => {
    context.ref.current.fileBrowser.selectedItem = item;

    if (item === null || item.isFolder) {
        return null;
    }

    try {
        const content = await loadFile({
            context,
            path: item.data.path,
        });

        debug.editor(`Loaded file content: ${content}`);

        if (content === null || content === undefined) {
            throw new Error('Could not load file from file system');
        }

        return {
            content,
            baseName: item.data.name,
            path: item.data.path,
        };
    } catch (e) {
        const message = getFormattedErrorMessage(
            e,
            `Failed to load file ${item.data.path}`,
        );

        context.showCustomAlert({
            title: 'Error',
            message,
            duration: 5000,
            variant: 'error',
        });

        return null;
    }
};

// --- Download / Upload ---

export const downloadFile = async ({
    actorRef,
    context,
}: {
    actorRef: EditorActorRef;
    context: EditorContext;
}) => {
    const {
        fileBrowser: { treeEnvironmentRef, items },
    } = context;

    const treeViewState =
        treeEnvironmentRef?.current?.viewState?.[FILE_BROWSER_TREE_ID] ?? null;

    const selectedItems = treeViewState?.selectedItems ?? null;

    if (selectedItems === null) {
        context.showCustomAlert({
            title: 'Download File Issue',
            message: 'Please select a file to download.',
            variant: 'warning',
        });
        return false;
    }

    for (const selectedItem of selectedItems) {
        const item = items?.[selectedItem] ?? null;
        if (item === null) break;

        if (item.isFolder) {
            context.showCustomAlert({
                title: 'Download File Issue',
                message: 'Folders cannot be downloaded.',
                variant: 'warning',
            });
            break;
        }

        const path =
            typeof selectedItem === 'string'
                ? selectedItem
                : selectedItem.toString();

        try {
            const content = await loadFile({ context, path });

            if (content === null || content === undefined) break;

            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.data.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            const message = getFormattedErrorMessage(
                e,
                'Failed to download file',
            );

            context.showCustomAlert({
                title: 'Download File Error',
                message,
                variant: 'error',
            });
        }
    }

    return true;
};

export const uploadFile = async ({
    content,
    context,
    filename,
}: {
    filename: string;
    content: string;
    context: EditorContext;
}) => {
    await saveFile({ autoSave: false, path: filename, content, context });
    debug.editor(`File ${filename} uploaded successfully`);
};

export const handleFileUpload = async ({
    context,
    event,
}: {
    event: ChangeEvent<HTMLInputElement>;
    context: EditorContext;
}) => {
    const files = event.target.files;
    const {
        fileBrowser: { items },
    } = context;

    const uploads: { filename: string; content: string; size: number }[] = [];
    const errors: { error: Error; filename: string; content: string }[] = [];

    if (files && files.length > 0) {
        for (const file of Array.from(files)) {
            const { size, name: filename } = file;
            const content = await file.text();

            try {
                await uploadFile({ filename, content, context });
                uploads.push({ filename, content, size });
            } catch (error) {
                if (error instanceof Error) {
                    errors.push({ error, filename, content });
                }
            }
        }
    }

    if (!items[FILE_BROWSER_ROOT_INDEX]) {
        items[FILE_BROWSER_ROOT_INDEX] = FILE_BROWSER_ROOT_ITEM;
    }

    if (uploads.length > 0) {
        for (const { filename, content, size } of uploads) {
            if (
                context.fileBrowser.treeEnvironmentRef.current &&
                context.ref.current.fileBrowser.fileCache
            ) {
                context.ref.current.fileBrowser.fileCache[filename] = {
                    type: 'file',
                    size,
                    content,
                };
            }
        }

        updateFileBrowser({ context });
    }
};

// --- Tree manipulation ---

export const addItemToTree = ({
    baseName,
    path,
    isFolder,
    context,
}: {
    baseName: string;
    path: string;
    isFolder: boolean;
    context: EditorContext;
}) => {
    const {
        fileBrowser: { dataProvider, items },
    } = context;

    const segments = path.split('/').filter((segment) => segment.length > 0);
    const treePathsToUpdate = [] as string[];

    if (segments.length === 1) {
        items?.[FILE_BROWSER_ROOT_INDEX]?.children?.push(path);
        treePathsToUpdate.push(FILE_BROWSER_ROOT_INDEX);
    } else if (segments.length > 1) {
        const parentPath = `${segments.slice(0, segments.length - 1).join('/')}/`;

        if (items[parentPath] !== undefined) {
            if (items[parentPath].children === undefined) {
                items[parentPath].children = [];
            }

            items[parentPath].children.push(path);
            treePathsToUpdate.push(parentPath);
        }
    }

    const newItem = {
        index: path,
        isFolder,
        data: {
            name: isFolder
                ? baseName.endsWith('/')
                    ? baseName.slice(0, -1)
                    : baseName
                : baseName,
            size: 0,
            path,
        },
    };

    items[path] = newItem;

    dataProvider.onDidChangeTreeDataEmitter.emit(treePathsToUpdate);
};

export const removeItemFromTree = ({
    context,
    item,
}: {
    context: EditorContext;
    item: FileBrowserItem;
}) => {
    const {
        fileBrowser: { dataProvider, items },
    } = context;

    delete items[item.index];

    let parentIndex: string | number | null = null;

    for (const entry of Object.values(items)) {
        if (entry.children?.includes(item.index)) {
            entry.children = entry.children.filter(
                (child: string | number) => child !== item.index,
            );
            parentIndex = entry.index;
            break;
        }
    }

    if (parentIndex !== null) {
        dataProvider.onDidChangeTreeDataEmitter.emit([parentIndex]);
    }
};

export const selectNextAvailableItem = ({
    context,
    path,
}: {
    context: EditorContext;
    path?: string;
}) => {
    const {
        fileBrowser: { treeRef },
        textEditor: { openFiles },
    } = context;
    const tree = treeRef.current;
    const openFilesArray = Array.from(openFiles.values());
    const nextOpenFile = openFilesArray[openFilesArray.length - 1];

    if (tree) {
        if (nextOpenFile) {
            tree.selectItems([nextOpenFile.path]);
        } else if (path) {
            tree.toggleItemSelectStatus(path);
        }
    }
};
